import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as combinedInstrumentsApi from "@/lib/api/combined-instruments";

export function useCombinedInstruments(page: number = 1, pageSize: number = 20) {
  return useQuery({
    queryKey: ["combinedInstruments", page, pageSize],
    queryFn: () => combinedInstrumentsApi.list(page, pageSize),
    placeholderData: keepPreviousData,
  });
}

export function useCombinedInstrument(id: string | null) {
  return useQuery({
    queryKey: ["combinedInstruments", id],
    queryFn: () => combinedInstrumentsApi.getById(id!),
    enabled: !!id,
  });
}

export function useCombinedInstrumentStatistics(id: string | null) {
  return useQuery({
    queryKey: ["combinedInstruments", id, "statistics"],
    queryFn: () => combinedInstrumentsApi.getStatistics(id!),
    enabled: !!id,
  });
}

export function useCombinedInstrumentChartData(id: string | null) {
  return useQuery({
    queryKey: ["combinedInstruments", id, "chartData"],
    queryFn: () => combinedInstrumentsApi.getChartData(id!),
    enabled: !!id,
  });
}

export function useCreateCombinedInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: combinedInstrumentsApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["combinedInstruments"] }),
  });
}

export function useUpdateCombinedInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof combinedInstrumentsApi.update>[1];
    }) => combinedInstrumentsApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["combinedInstruments"] }),
  });
}

export function useDeleteCombinedInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: combinedInstrumentsApi.remove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["combinedInstruments"] }),
  });
}
